import { isHash } from 'viem'

import { toCursorPage } from '@/helpers/cursorPage'
import { orpc } from '@/lib/orpc/client'
import { errorCodeOf } from '@/lib/orpc/errors'

import type { MarketLiquidationsInput, MarketLiquidationsOutput } from '../liquidations.contract'

/** The procedure's input without the cursor; `marketId` is the page's route param. */
export type MarketLiquidationsFilters = Omit<MarketLiquidationsInput, 'cursor' | 'marketId'> & {
  marketId: string
}

/** A market the bridge does not know has no liquidations: the table shows its empty state. */
export const EMPTY_MARKET_LIQUIDATIONS: MarketLiquidationsOutput = {
  items: [],
  partial: false,
  nextCursor: null
}

// Unset filters are dropped so the server's first-page key and the hook's default key hash alike.
const definedFilters = (filters: MarketLiquidationsFilters) =>
  Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== undefined))

export const marketLiquidationsTableQueryKey = (filters: MarketLiquidationsFilters) =>
  [...orpc.market.liquidations.key({ type: 'infinite' }), definedFilters(filters)] as const

export async function fetchMarketLiquidationsPage(
  filters: MarketLiquidationsFilters,
  cursor: string | undefined
) {
  const { marketId } = filters
  if (!isHash(marketId)) return toCursorPage(EMPTY_MARKET_LIQUIDATIONS)
  try {
    return toCursorPage(await orpc.market.liquidations.call({ ...filters, marketId, cursor }))
  } catch (error) {
    if (errorCodeOf(error) === 'NOT_FOUND') return toCursorPage(EMPTY_MARKET_LIQUIDATIONS)
    throw error
  }
}
